import { useCallback, useMemo, useState } from "react";
import moment from "moment-timezone";
import { File as FileIcon, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable/index";
import {
  type DestinationPacketDocument,
  type DestinationPacketInstance,
  useListDestinationPacketInstances,
  useListDestinationPacketTemplates,
} from "@/lib/api/destinationPackets";
import { getSigningStatusLabel } from "@/lib/api/signingContracts";
import { CommonDestinationTimezone } from "@/lib/utils";
import { RoutePath } from "@/section/router";

function formatDocumentVariant(variant?: string | null): string {
  if (!variant) return "";
  return variant
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function getTemplateLabel(doc?: DestinationPacketDocument | null): string {
  if (!doc) {
    return "";
  }

  return doc.DocumentTemplate?.title ?? formatDocumentVariant(doc.document_variant);
}

export function DestinationPacketsInstancesPage() {
  const navigate = useNavigate();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");

  const templatesResult = useListDestinationPacketTemplates();
  const templates = useMemo<DestinationPacketDocument[]>(
    () => templatesResult.data?.data?.data ?? [],
    [templatesResult.data?.data?.data],
  );

  const templateOptions = useMemo(
    () => [
      { value: "", label: "All Templates" },
      ...templates.map((template) => ({
        value: template.id,
        label: getTemplateLabel(template) || "Untitled Template",
      })),
    ],
    [templates],
  );

  const instancesResult = useListDestinationPacketInstances(
    selectedTemplateId ? { template_id: selectedTemplateId } : undefined,
  );
  const instances = useMemo(
    () => instancesResult.data?.data ?? [],
    [instancesResult.data?.data],
  );

  const navigateToPreview = useCallback(
    (instance: DestinationPacketInstance, type: "sent" | "final") => {
      const templateId = instance.destination_packet_document_id;
      const instanceId = instance.id;

      if (!templateId || !instanceId) {
        return;
      }

      const pathTemplate =
        type === "sent"
          ? RoutePath.DestinationPacketsInstanceSentPreview
          : RoutePath.DestinationPacketsInstanceFinalPreview;

      const path = pathTemplate
        .replace(":template_id", templateId)
        .replace(":instance_id", instanceId);

      navigate(path);
    },
    [navigate],
  );

  const columns = useMemo<DataTableColumn<DestinationPacketInstance>[]>(
    () => [
      {
        id: "template_name",
        header: "Template",
        accessorFn: (row) => getTemplateLabel(row.DestinationPacketDocument),
      },
      {
        id: "packet_name",
        header: "Packet",
        accessorFn: (row) =>
          row.DestinationPacketDocument?.DestinationTemplatePacket?.name ??
          row.DestinationPacketEnvelop?.DestinationTemplatePacket?.name ??
          "",
      },
      {
        id: "customer_name",
        header: "Customer",
        accessorFn: (row) => row.DestinationPacketEnvelop?.Customer?.long_name ?? "",
      },
      {
        id: "lender_name",
        header: "Lender",
        accessorFn: (row) => row.DestinationPacketEnvelop?.lender_name ?? "",
      },
      {
        id: "signing_status",
        header: "Status",
        accessorFn: (row) => row.DocumentTemplateInstance?.SigningSession?.last_status ?? "",
        cell: (info) => getSigningStatusLabel((info.getValue() as string) || ""),
      },
      {
        id: "created_at",
        header: "Created",
        accessorKey: "created_at",
        cell: (info) =>
          moment
            .tz(info.getValue() as string, CommonDestinationTimezone)
            .format("YYYY-MM-DD HH:mm"),
      },
      {
        id: "sent_preview",
        header: "Sent Document",
        enableSorting: false,
        enableFiltering: false,
        cell: ({ row }) => {
          const sentDocumentId =
            row.original.DocumentTemplateInstance?.SigningSession?.sent_document_id;

          return (
            <Button
              variant="ghost"
              className="h-8 w-8 p-0"
              onClick={() => navigateToPreview(row.original, "sent")}
              disabled={!sentDocumentId}
            >
              <span className="sr-only">Preview sent document</span>
              <FileIcon className="h-4 w-4" />
            </Button>
          );
        },
      },
      {
        id: "final_preview",
        header: "Final Document",
        enableSorting: false,
        enableFiltering: false,
        cell: ({ row }) => {
          const finalDocumentId =
            row.original.DocumentTemplateInstance?.SigningSession?.final_document_id;

          return (
            <Button
              variant="ghost"
              className="h-8 w-8 p-0"
              onClick={() => navigateToPreview(row.original, "final")}
              disabled={!finalDocumentId}
            >
              <span className="sr-only">Preview final document</span>
              <FileIcon className="h-4 w-4" />
            </Button>
          );
        },
      },
    ],
    [navigateToPreview],
  );

  const isLoading = instancesResult.isLoading;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm text-muted-foreground uppercase tracking-wide">
            Destination Packets
          </p>
          <h2 className="text-2xl font-bold">Instances</h2>
        </div>
        <Button variant="outline" onClick={() => navigate(RoutePath.DestinationPacketsSendEnvelope)}>
          <Send className="mr-2 h-4 w-4" />
          Send Envelope
        </Button>
      </div>


      <DataTable
        stickyHeaders
        tableClassName="max-h-[calc(80vh-200px)] overflow-y-auto"
        data={instances}
        columns={columns}
        enableSorting
        enableFiltering
        filterStyle="select"
        filterMode="internal"
        defaultSorting={[{ id: "created_at", desc: true }]}
        emptyMessage={isLoading ? "Loading…" : "No instances found"}
      />
    </div>
  );
}

